import classNames from "classnames";
import { FC, PropsWithChildren, ReactNode, TdHTMLAttributes, useEffect, useRef, useState } from "react";
import { UseListFiltersReturn } from "../../hooks/useListFilters";
import useI18n from "../../hooks/useTranslations";
import ListItemMenu, { Action } from "./ListItemMenu";
import Pagination from "./Pagination";
import styles from "./Table.module.css";
import { TableEmptyState, TableErrorState, TableLoadingState } from "./TableStatus";

interface Props<T> {
  className?: string;
  filters?: UseListFiltersReturn;
  total?: number;
  head?: ReactNode;
  items?: T[];
  renderRow: (item: T) => ReactNode;
  rowActions?: (item: T) => Action[];
  onRowClick?: (item: T, event: React.MouseEvent<HTMLTableRowElement>) => void;
  footer?: ReactNode;
  isTall?: boolean;
  error?: boolean;
}

const Table = <T,>({
  className,
  filters,
  total = -1,
  head,
  items,
  renderRow,
  rowActions,
  onRowClick,
  footer,
  isTall = false,
  error,
}: Props<T>) => {
  const i18n = useI18n();
  const page = filters?.state.page ?? 1;
  const limit = filters?.state.limit ?? 5;

  const fromItem = (page - 1) * limit + 1;
  const maxToItem = fromItem + limit - 1;

  const [data, setData] = useState<{ items: T[]; total: number }>();

  useEffect(() => {
    !!items && setData({ items, total });
  }, [items]);

  const toItem = Math.max(Math.min(maxToItem, data?.total ?? 0), 0);

  const clickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  return (
    <>
      <div className={styles.tableWrapper}>
        <table
          className={classNames(className, styles.table, {
            [styles.tall]: isTall,
            [styles.noRows]: !data?.items?.length,
          })}
        >
          {head && <thead>{head}</thead>}
          {!error && !!data?.items && (
            <>
              <tbody>
                {data?.items?.map((item, index) => {
                  return (
                    <tr
                      key={`table_row_${index}`}
                      onClick={
                        onRowClick
                          ? (event) => {
                              const selection = window.getSelection();
                              const selectedText = selection?.toString();
                              if (selectedText?.length) {
                                return; // user is selecting text, don't trigger click
                              }
                              // Delay to see if a double-click is coming
                              clickTimeoutRef.current = setTimeout(() => {
                                onRowClick(item, event);
                              }, 200); // 200ms delay (typical double-click threshold)
                            }
                          : undefined
                      }
                      onDoubleClick={
                        onRowClick
                          ? () => {
                              if (clickTimeoutRef.current) {
                                clearTimeout(clickTimeoutRef.current);
                                clickTimeoutRef.current = null;
                              }
                            }
                          : undefined
                      }
                      className={classNames({ [styles.clickable]: !!onRowClick })}
                    >
                      {renderRow?.(item)}
                      {rowActions ? (
                        <td width={1}>
                          <ListItemMenu item={item} actions={rowActions(item)} />
                        </td>
                      ) : (
                        <></>
                      )}
                    </tr>
                  );
                })}
              </tbody>
              {footer && <tfoot>{footer}</tfoot>}
            </>
          )}
        </table>
      </div>

      {!error && !!data?.items && data.total === 0 && <TableEmptyState message={i18n.translation.common.noItemsFound} />}

      {!error && !data?.items && <TableLoadingState />}

      {error && <TableErrorState message={i18n.translation.common.errorLoadingData} />}

      {!error && !!data?.items && data.total > 0 && (
        <Pagination
          from={fromItem}
          to={toItem}
          total={data.total}
          onPrevious={() => filters?.prevPage()}
          onNext={() => filters?.nextPage()}
          canGoPrevious={page !== 1}
          canGoNext={toItem !== data.total}
          previousLabel={i18n.translation.common.previousNRows(limit)}
          nextLabel={i18n.translation.common.nextNRows(limit)}
          showNavigation={data.total > limit}
        />
      )}
    </>
  );
};

type TdProps = TdHTMLAttributes<HTMLTableCellElement> &
  PropsWithChildren<{
    noWrap?: boolean;
  }>;

const Td: FC<TdProps> = ({ children, noWrap, ...rest }) => (
  <td className={noWrap ? styles.noWrap : undefined} {...rest}>
    {children}
  </td>
);

Table.Td = Td;

const IconTd = ({ children }: PropsWithChildren) => (
  <td className={styles.iconTd}>
    <div>{children}</div>
  </td>
);

Table.IconTd = IconTd;

export default Table;
